import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { themeApi } from '@/api/theme.api';
import { getAllSectorQuestions } from '@/api/sector-questions.api';
import { answeredAudioQuestionIds, orderQuestions, type PastContribution } from '../initial-data.helpers';
import type { InitialDataCatalog } from '../types';

const EMPTY_IDS: ReadonlySet<string> = new Set();
const CONTRIBUTIONS_PAGE_SIZE = 500;
// Safety stop: 20 pages of 500 is far above what one contributor records in a sector.
const MAX_CONTRIBUTION_PAGES = 20;

type ContributionsPage = { data?: PastContribution[]; pagination?: { pages?: number } };

/**
 * Every contribution of the user in the sector. The endpoint filters by user
 * and sector only, newest first, so a single page could hide older answers in
 * the chosen language behind answers in other languages.
 */
async function fetchSectorContributions(sectorId: string): Promise<PastContribution[]> {
  const all: PastContribution[] = [];
  for (let page = 1; page <= MAX_CONTRIBUTION_PAGES; page += 1) {
    const response = (await themeApi.getContributionsBySector(sectorId, page, CONTRIBUTIONS_PAGE_SIZE)) as unknown as ContributionsPage;
    const rows = response.data ?? [];
    all.push(...rows);
    const pages = response.pagination?.pages;
    if (rows.length < CONTRIBUTIONS_PAGE_SIZE || (typeof pages === 'number' && page >= pages)) break;
  }
  return all;
}

/**
 * Sectors, the ordered questions of the chosen sector, and, when asked, the
 * questions this contributor already answered in that language.
 */
export function useInitialDataCatalog(
  sectorId: string,
  language: string,
  options: { trackSubmitted: boolean },
): InitialDataCatalog {
  const sectorsQuery = useQuery({
    queryKey: ['initial-data', 'sectors'],
    queryFn: async () => (await themeApi.getSectors(1, 100)).data ?? [],
    staleTime: 5 * 60_000,
  });

  const ready = Boolean(sectorId && language);

  const questionsQuery = useQuery({
    queryKey: ['initial-data', 'questions', sectorId, language],
    queryFn: ({ signal }) => getAllSectorQuestions(sectorId, language, signal),
    enabled: ready,
  });

  const submittedQuery = useQuery({
    queryKey: ['initial-data', 'submitted', sectorId, language],
    queryFn: async () => answeredAudioQuestionIds(await fetchSectorContributions(sectorId), language),
    enabled: ready && options.trackSubmitted,
  });

  const questions = useMemo(() => orderQuestions(questionsQuery.data ?? []), [questionsQuery.data]);

  return {
    sectors: sectorsQuery.data ?? [],
    questions,
    submittedQuestionIds: submittedQuery.data ?? EMPTY_IDS,
    isLoadingSectors: sectorsQuery.isLoading,
    isLoadingQuestions: ready && (questionsQuery.isLoading || (options.trackSubmitted && submittedQuery.isLoading)),
    sectorsError: sectorsQuery.isError,
    questionsError: questionsQuery.isError || submittedQuery.isError,
    retry: () => {
      void sectorsQuery.refetch();
      if (ready) {
        void questionsQuery.refetch();
        if (options.trackSubmitted) void submittedQuery.refetch();
      }
    },
  };
}
